import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, Hash, MapPin, ShieldCheck, BadgeCheck, Building2 } from "lucide-react";
import { Reveal, EASE } from "@/components/Reveal";

const FACTS = [
  { icon: Hash, label: "CIN", value: "U55101RJ2025PTC109155" },
  { icon: CalendarDays, label: "Incorporated", value: "December 2, 2025" },
  { icon: MapPin, label: "Registered office", value: "Vaishali Nagar, Jaipur, Rajasthan" },
  { icon: Building2, label: "Structure", value: "Private Limited Company" },
];

const PROMOTERS = [
  {
    initials: "SK",
    name: "Satyajit Kaliprasad",
    role: "IT Solution Architect & Owner",
    share: 90,
    tone: "cyan",
    line: "Architects the Mozekaql platform stack and leads the company's technology direction.",
  },
  {
    initials: "K",
    name: "Kanchan",
    role: "Diploma in Fashion Technology",
    share: 10,
    tone: "amber",
    line: "Brings design discipline and craft sensibility to the kitchen's brands and experiences.",
  },
];

const TIMELINE = [
  { date: "Dec 2, 2025", title: "TOSIASI Creatives incorporated", copy: "Registered in Jaipur, Rajasthan with a dual ambition: food and software." },
  { date: "2025 — 26", title: "Nok Chhonk goes live", copy: "The Odia & Bengali cloud kitchen opens in Vaishali Nagar, now on Swiggy & Zomato." },
  { date: "December 2026", title: "Mozekaql launches", copy: "The IT solution architecture and agro-research vertical comes out of the lab." },
];

export default function About() {
  useEffect(() => {
    document.title = "About Us — TOSIASI CREATIVES PRIVATE LIMITED";
  }, []);

  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative pt-40 pb-20 md:pt-48">
        <div className="glow-amber pointer-events-none absolute -left-40 top-0 h-[520px] w-[520px] opacity-70" />
        <div className="glow-cyan pointer-events-none absolute -right-52 top-32 h-[560px] w-[560px] opacity-70" />
        <div className="relative mx-auto max-w-7xl px-6">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="eyebrow"
          >
            Corporate profile
          </motion.p>
          <h1 className="mt-8 max-w-3xl font-display text-[clamp(2.4rem,6vw,4.6rem)] font-semibold leading-[1.05] tracking-tight text-white">
            {["Built in Jaipur. Run like", "a kitchen — and a lab."].map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={i === 1 ? "block font-light text-slate-400" : "block"}
                  initial={{ y: "112%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1, delay: 0.2 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
            className="mt-8 max-w-2xl text-base leading-relaxed text-slate-400 md:text-lg"
          >
            TOSIASI CREATIVES PRIVATE LIMITED is a venture company where an operating food business and a software
            practice fund each other — kitchen revenue builds platforms, and platforms give back to the soil and the
            community.
          </motion.p>
        </div>
      </section>

      {/* FACTS */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {FACTS.map((f, i) => (
              <Reveal key={f.label} delay={0.06 * i}>
                <div className="glass card-hover h-full rounded-3xl p-7" data-testid={`about-fact-${i}`}>
                  <f.icon size={20} className="text-amber-400" />
                  <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">{f.label}</p>
                  <p className="mt-2 break-words text-sm font-medium text-slate-100">{f.value}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CREDENTIALS */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="eyebrow">Credentials</p>
            <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-white md:text-4xl">
              Licensed, recognized, <span className="font-light text-slate-400">and just getting started.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            <Reveal>
              <div className="glass card-hover relative h-full overflow-hidden rounded-3xl p-10" data-testid="credential-fssai">
                <div className="glow-amber pointer-events-none absolute -right-20 -top-20 h-64 w-64 opacity-70" />
                <ShieldCheck size={28} className="text-amber-400" />
                <h3 className="mt-6 font-display text-2xl font-semibold text-white">FSSAI Licensed</h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
                  Our kitchen operations are licensed under the Food Safety and Standards Authority of India — hygiene
                  and compliance come before scale.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="glass card-hover relative h-full overflow-hidden rounded-3xl p-10" data-testid="credential-startup-india">
                <div className="glow-cyan pointer-events-none absolute -right-20 -top-20 h-64 w-64 opacity-70" />
                <BadgeCheck size={28} className="text-cyan-400" />
                <h3 className="mt-6 font-display text-2xl font-semibold text-white">Startup India Recognized</h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
                  Recognized under the Startup India initiative — building food-tech and platform IP from Jaipur, not
                  from a metro.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROMOTERS */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="eyebrow">The promoters</p>
            <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-white md:text-4xl">
              Two people, <span className="font-light text-slate-400">one share of the risk.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {PROMOTERS.map((p, i) => (
              <Reveal key={p.name} delay={0.08 * i}>
                <div className="glass card-hover relative h-full overflow-hidden rounded-3xl p-10" data-testid={`promoter-${i}`}>
                  <div
                    className={`pointer-events-none absolute -left-16 -top-16 h-56 w-56 ${
                      p.tone === "cyan" ? "glow-cyan" : "glow-amber"
                    } opacity-70`}
                  />
                  <div className="flex items-center gap-5">
                    <div
                      className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border font-display text-lg font-semibold ${
                        p.tone === "cyan"
                          ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-300"
                          : "border-amber-400/40 bg-amber-400/10 text-amber-300"
                      }`}
                    >
                      {p.initials}
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-semibold text-white">{p.name}</h3>
                      <p className="mt-1 text-sm text-slate-400">{p.role}</p>
                    </div>
                  </div>
                  <p className="mt-6 text-sm leading-relaxed text-slate-400">{p.line}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="eyebrow">Trajectory</p>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.title} delay={0.08 * i}>
                <div className="glass card-hover h-full rounded-3xl p-8" data-testid={`about-milestone-${i}`}>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400">{t.date}</p>
                  <h3 className="mt-4 font-display text-xl font-semibold text-white">{t.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{t.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="about-cta" className="btn btn-amber">
                Talk to the promoters <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
